import React from "react";
import clsx from "clsx";
import { motion } from "framer-motion";
import { cn } from "../../../lib/utils/cn";

interface TabItem {
  label: string;
  value: string;
}

interface AnimatedTabsProps {
  tabs: TabItem[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  /** 한 번에 보여줄 최대 탭 개수 (예: 5개) */
  maxVisible?: number;
}

const AnimatedCoverTabs: React.FC<AnimatedTabsProps> = ({
  className,
  tabs,
  value,
  onChange,
  maxVisible, // 새로 추가
}) => {
  // 1. 현재 선택된 탭의 인덱스 (없으면 0으로)
  const activeIndex = React.useMemo(() => {
    const idx = tabs.findIndex((t) => t.value === value);
    return idx === -1 ? 0 : idx;
  }, [tabs, value]);

  // 2. 실제로 한 번에 보여줄 개수 (maxVisible이 없으면 전체 길이)
  const windowSize = Math.min(maxVisible ?? tabs.length, tabs.length);

  // 3. 시작 인덱스 계산 (선택된 탭을 가운데에 두려고 시도)
  const startIndex = React.useMemo(() => {
    // 예: windowSize = 5 → 가운데 인덱스는 2
    const half = Math.floor(windowSize / 2);

    // 기본은 activeIndex - half
    let start = activeIndex - half;

    // 0보다 작아지지 않게
    if (start < 0) start = 0;

    // 끝을 넘지 않게 (tabs.length - windowSize까지)
    const maxStart = tabs.length - windowSize;
    if (start > maxStart) start = maxStart;

    return start;
  }, [activeIndex, windowSize, tabs.length]);

  // 4. 실제로 화면에 렌더링할 탭들
  const visibleTabs = React.useMemo(
    () => tabs.slice(startIndex, startIndex + windowSize),
    [tabs, startIndex, windowSize],
  );

  // 5. 애니메이션 배경(인디케이터)이 visibleTabs 안에서 몇 번째인지
  const indicatorIndex = React.useMemo(() => {
    const idx = visibleTabs.findIndex((t) => t.value === value);
    return idx === -1 ? 0 : idx;
  }, [visibleTabs, value]);

  return (
    <div
      className={cn(
        "relative flex bg-[#e6e6e6] rounded-full w-full h-[44px] p-[4px]",
        className,
      )}
    >
      {/* 버튼들 */}
      {visibleTabs.map((tab) => {
        const isActive = tab.value === value;

        return (
          <button
            key={tab.value}
            onClick={() => onChange(tab.value)}
            className={clsx(
              "relative z-10 text-[16px] font-bold transition-colors text-center flex-1",
              isActive ? "text-white" : "text-[#888]",
            )}
          >
            {tab.label}
          </button>
        );
      })}

      {/* 선택된 탭 뒤에 깔리는 애니메이션 배경 */}
      <motion.div
        layout
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="absolute z-0 rounded-full bg-[#393E46]"
        style={{
          // 한 줄에 windowSize 개 → 각 탭의 너비
          width: `calc(${100 / visibleTabs.length}% - 8px)`,
          height: "calc(100% - 8px)",
          // indicatorIndex를 기준으로 left 계산
          left: `calc(${(indicatorIndex * 100) / visibleTabs.length}% + 4px)`,
        }}
      />
    </div>
  );
};

export default AnimatedCoverTabs;
